'use client';

import React, { useState, useEffect } from 'react';

interface BottomToastProps {
  title: string;
  description?: string;
  duration?: number;
  bottomOffset?: string;
}

interface ToastConfig {
  maxToasts?: number;
  defaultBottomOffset?: string;
}

interface ToastState extends BottomToastProps {
  id: string;
  isVisible: boolean;
}

let toastId = 0;
const toasts: ToastState[] = [];
const listeners: ((toasts: ToastState[]) => void)[] = [];
let config: ToastConfig = { maxToasts: 5, defaultBottomOffset: "100px" };

function notifyListeners() {
  listeners.forEach(listener => listener([...toasts]));
}

function addToast(toast: BottomToastProps) {
  const id = (++toastId).toString();
  const newToast: ToastState = {
    ...toast,
    id,
    isVisible: false,
    duration: toast.duration || 3000,
    bottomOffset: toast.bottomOffset || config.defaultBottomOffset || "100px",
  };

  // Check if we need to remove oldest toast to maintain max limit
  if (config.maxToasts && toasts.length >= config.maxToasts) {
    const oldestToast = toasts[0];
    if (oldestToast) {
      dismissToast(oldestToast.id);
    }
  }

  toasts.push(newToast);
  notifyListeners();

  // Show toast after a small delay for entrance animation
  setTimeout(() => {
    const toastIndex = toasts.findIndex(t => t.id === id);
    if (toastIndex !== -1) {
      toasts[toastIndex].isVisible = true;
      notifyListeners();
    }
  }, 50);

  // Auto dismiss
  setTimeout(() => {
    dismissToast(id);
  }, newToast.duration);

  return id;
}

function dismissToast(id: string) {
  const toastIndex = toasts.findIndex(t => t.id === id);
  if (toastIndex !== -1) {
    toasts[toastIndex].isVisible = false;
    notifyListeners();

    // Remove from array after animation
    setTimeout(() => {
      const finalIndex = toasts.findIndex(t => t.id === id);
      if (finalIndex !== -1) {
        toasts.splice(finalIndex, 1);
        notifyListeners();
      }
    }, 300);
  }
}

export function toast(options: BottomToastProps) {
  return addToast(options);
}

export function configureToast(newConfig: ToastConfig) {
  config = { ...config, ...newConfig };
}

export function dismissAllToasts() {
  const currentToasts = [...toasts];
  currentToasts.forEach((toast, index) => {
    setTimeout(() => {
      dismissToast(toast.id);
    }, index * 100); // 100ms delay between each dismissal for sequential effect
  });
}

export function dismissToastsSequentially(count?: number) {
  const toastsToDismiss = count ? toasts.slice(0, count) : [...toasts];
  toastsToDismiss.forEach((toast, index) => {
    setTimeout(() => {
      dismissToast(toast.id);
    }, index * 100); // 100ms delay between each dismissal
  });
}

function BottomToast({ toast }: { toast: ToastState }) {
  return (
    <div
      className={`
        fixed left-1/2 transform -translate-x-1/2 z-50
        transition-all duration-300 ease-in-out
        ${toast.isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-0 pointer-events-none'
        }
      `}
      inert
      style={{
        bottom: `${toast.bottomOffset}`,
      }}
    >
      <div className="mx-auto relative w-screen">
        <div className="bg-[rgba(0,0,0,0.9)] rounded-[100px] px-4 py-3 mx-auto opacity-85 w-fit max-w-[80%] shadow-[2px_2px_4px_0_rgba(0,0,0,0.25)]">
          <h3 className="text-[15px] font-pretendard font-semibold text-white leading-normal origin-bottom-center text-center break-all whitespace-break-spaces">
            {toast.title}
          </h3>
          {toast.description && (
            <p className="text-[15px] font-pretendard font-normal text-white leading-normal mt-1">
              {toast.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BottomToastProvider({
  maxToasts = 5,
  defaultBottomOffset = "100px"
}: {
  maxToasts?: number;
  defaultBottomOffset?: string;
} = {}) {
  const [currentToasts, setCurrentToasts] = useState<ToastState[]>([]);

  useEffect(() => {
    // Set config when provider mounts
    config = { ...config, maxToasts, defaultBottomOffset };
  }, [maxToasts, defaultBottomOffset]);

  useEffect(() => {
    const listener = (newToasts: ToastState[]) => {
      setCurrentToasts(newToasts);
    };

    listeners.push(listener);

    return () => {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  return (
    <>
      {currentToasts.map(toast => (
        <BottomToast key={toast.id} toast={toast} />
      ))}
    </>
  );
}